import { useEffect, useRef, useState } from "react";
import {
  getHandle,
  hasHandle,
  HandleModal,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useLeaderboard,
  type SubmitResult,
} from "@karrtopelka/game-kit";
import type { CardProps } from "@karrtopelka/cards/types";

import { Board } from "./components/Board.js";
import { buildAimCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import { useAimStore } from "./store.js";

/** one shared leaderboard instance: desktop rail + mobile summary board. */
type Lb = ReturnType<typeof useLeaderboard>;

const MORE_GAMES = [
  { name: "wheel", hook: "push your luck", href: "https://wheel.karrtopelka.com" },
  { name: "reflex", hook: "pure reaction time", href: "https://reflex.karrtopelka.com" },
  { name: "crash", hook: "cash out or bust", href: "https://crash.karrtopelka.com" },
] as const;

export function App() {
  const phase = useAimStore((s) => s.phase);

  // Funnel beacons: one page_view per load, one play_start per session, one
  // play_done per finished round.
  useEffect(() => trackPageView("aim"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "running" && !started.current) {
      started.current = true;
      track("aim", "play_start");
    }
    if (phase === "done") track("aim", "play_done");
  }, [phase]);

  // Lifted so the desktop rail sees scores during idle/running too (one fetch,
  // shared with the mobile post-game board).
  const lb = useLeaderboard("aim");
  const [handle, setHandle] = useState<string | null>(() => getHandle());

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:grid-rows-[auto_1fr_auto] lg:items-start lg:justify-center lg:gap-x-10 lg:gap-y-8 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <CrosshairBackdrop />

      <header className="flex animate-fade-up flex-col items-center lg:col-span-3">
        <h1 className="font-display text-3xl font-bold tracking-tight lg:text-5xl">
          aim
          <span className="text-accent drop-shadow-[0_0_14px_rgba(255,111,165,0.6)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:text-base">
          tap every target. 20 seconds.
        </p>
      </header>

      <LeftRail />

      {phase === "done" ? (
        <Summary lb={lb} handle={handle} onHandle={setHandle} />
      ) : (
        <Play />
      )}

      <RightRail lb={lb} handle={handle} />

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-0 lg:w-full"
        style={{ animationDelay: "320ms" }}
      >
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://wheel.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try wheel — push your luck →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>

        <div className="hidden lg:flex lg:flex-col lg:items-center lg:gap-3">
          <span className="text-[10px] uppercase tracking-widest text-text-faint">
            more games
          </span>
          <div className="flex items-stretch gap-3">
            {MORE_GAMES.map((g) => (
              <a
                key={g.name}
                href={g.href}
                className="group flex min-w-[9rem] flex-col gap-0.5 rounded-lg border border-border bg-surface/60 px-4 py-2.5 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
              >
                <span className="font-display text-sm font-bold text-text transition-colors group-hover:text-accent">
                  {g.name}
                  <span className="text-accent">.</span>
                </span>
                <span className="text-[11px] text-text-faint">{g.hook}</span>
              </a>
            ))}
          </div>
          <a
            href="https://karrtopelka.com"
            className="transition-colors hover:text-accent"
          >
            karrtopelka.com
          </a>
        </div>
      </footer>
    </main>
  );
}

/**
 * Signature ambient motif: a faint sniper-scope crosshair centered behind the
 * board — two hairline rings (outer one dashed, drifting on a 48s rotation)
 * plus cross ticks. Fixed, pointer-transparent, ~0.07 opacity so OLED black
 * stays the read.
 */
function CrosshairBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 -z-10 flex items-center justify-center"
    >
      <svg
        viewBox="0 0 640 640"
        className="h-[min(84vmin,640px)] w-[min(84vmin,640px)]"
      >
        <circle
          cx="320"
          cy="320"
          r="150"
          fill="none"
          stroke="rgba(255,111,165,0.07)"
          strokeWidth="1"
        />
        <g stroke="rgba(255,111,165,0.09)" strokeWidth="1">
          <line x1="320" y1="24" x2="320" y2="88" />
          <line x1="320" y1="552" x2="320" y2="616" />
          <line x1="24" y1="320" x2="88" y2="320" />
          <line x1="552" y1="320" x2="616" y2="320" />
        </g>
        <circle cx="320" cy="320" r="3" fill="rgba(255,111,165,0.12)" />
      </svg>
      <svg
        viewBox="0 0 640 640"
        className="absolute h-[min(84vmin,640px)] w-[min(84vmin,640px)] animate-spin-slow"
      >
        <circle
          cx="320"
          cy="320"
          r="256"
          fill="none"
          stroke="rgba(255,111,165,0.08)"
          strokeWidth="1"
          strokeDasharray="4 18"
        />
      </svg>
    </div>
  );
}

/** desktop-only left rail: rules, personal numbers, keyboard hint. */
function LeftRail() {
  const best = useAimStore((s) => s.best);
  const hits = useAimStore((s) => s.hits);
  const phase = useAimStore((s) => s.phase);

  return (
    <aside
      className="sticky top-8 hidden animate-fade-up flex-col gap-4 lg:flex"
      style={{ animationDelay: "160ms" }}
    >
      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
          how it works
        </h2>
        <ul className="flex flex-col gap-1 text-sm text-text-muted">
          <li>one target at a time</li>
          <li>hit it — it respawns</li>
          <li>20 seconds. count your hits</li>
        </ul>
      </div>

      <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
          your best
        </h2>
        <p className="font-display text-4xl font-bold text-text">{best}</p>
        {phase !== "idle" && (
          <p className="mt-1 text-xs text-text-faint">
            this run{" "}
            <span className="font-display font-bold text-accent">{hits}</span>
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface/60 p-4 text-xs text-text-faint backdrop-blur">
        <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-[10px] text-text-muted">
          space
        </kbd>{" "}
        starts a round
      </div>
    </aside>
  );
}

/** desktop-only right rail: the board is always posted, even mid-round. */
function RightRail({ lb, handle }: { lb: Lb; handle: string | null }) {
  return (
    <aside
      className="sticky top-8 hidden animate-fade-up flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur lg:flex"
      style={{ animationDelay: "240ms" }}
    >
      <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        best aim
      </h2>
      <Leaderboard
        entries={lb.entries}
        loading={lb.loading}
        myHandle={handle}
        formatValue={(v) => String(v)}
        emptyLabel="no scores yet — set the bar."
        range={lb.range}
        onRange={lb.setRange}
      />
    </aside>
  );
}

function Play() {
  const phase = useAimStore((s) => s.phase);
  const hits = useAimStore((s) => s.hits);
  const timeLeftMs = useAimStore((s) => s.timeLeftMs);
  const best = useAimStore((s) => s.best);
  const start = useAimStore((s) => s.start);

  const secs = Math.ceil(timeLeftMs / 1000);
  const urgent = phase === "running" && secs <= 5;

  // Desktop nicety: space/enter starts the round. Tap-to-act game, no other
  // keyboard consumers in the idle phase, so this is trivially safe.
  useEffect(() => {
    if (phase !== "idle") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "Enter") {
        e.preventDefault();
        start();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, start]);

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-4 lg:max-w-xl lg:justify-self-center"
      style={{ animationDelay: "80ms" }}
    >
      {/* HUD */}
      <div className="flex w-full items-center justify-between font-display">
        <span className="text-lg lg:text-2xl">
          <span className="font-sans text-sm text-text-faint lg:text-base">
            hits{" "}
          </span>
          <span className="font-bold text-text">{hits}</span>
        </span>
        <span className="text-lg lg:text-2xl">
          <span
            className={`font-bold transition-colors ${
              urgent ? "animate-breathe text-danger" : "text-accent"
            }`}
          >
            {secs}
          </span>
          <span className="font-sans text-sm text-text-faint lg:text-base">s</span>
        </span>
      </div>

      <div className="relative w-full">
        <Board />
        {phase === "idle" && (
          <button
            type="button"
            onClick={start}
            className="group absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg bg-bg/60 transition-colors duration-200 hover:bg-bg/50"
          >
            <span className="animate-neon-pulse rounded-full border border-accent bg-surface px-6 py-2.5 text-xl font-bold text-text transition-transform duration-200 group-hover:-translate-y-0.5">
              <span className="lg:hidden">tap to start</span>
              <span className="hidden lg:inline">click to start</span>
            </span>
            <span className="text-sm text-text-muted">
              hit as many targets as you can
            </span>
            {best > 0 && (
              <span className="mt-1 text-xs text-text-faint">
                your best{" "}
                <span className="font-display font-bold text-accent">{best}</span>
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function Summary({
  lb,
  handle,
  onHandle,
}: {
  lb: Lb;
  handle: string | null;
  onHandle: (h: string) => void;
}) {
  const hits = useAimStore((s) => s.hits);
  const best = useAimStore((s) => s.best);
  const reset = useAimStore((s) => s.reset);

  const { entries, loading, submit, range, setRange } = lb;

  const [showHandle, setShowHandle] = useState(false);
  const [submitted, setSubmitted] = useState<SubmitResult | null>(null);
  const [card, setCard] = useState<CardProps | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);

  const isBest = hits >= best && hits > 0;

  const doSubmit = async () => {
    setPosting(true);
    setSubmitError(null);
    const res = await submit(hits);
    setPosting(false);
    const who = getHandle() ?? "you";
    if (!res) {
      setSubmitError("couldn't submit (rate limited?) — card still works");
      setCard(buildAimCard(hits, who, null));
      return;
    }
    setSubmitted(res);
    setCard(buildAimCard(hits, who, res));
  };

  const onPost = () => {
    if (hasHandle()) void doSubmit();
    else setShowHandle(true);
  };

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-6 lg:max-w-xl lg:justify-self-center"
      style={{ animationDelay: "80ms" }}
    >
      <section className="flex flex-col items-center">
        {isBest && (
          <span className="mb-2 animate-pop rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg">
            ⚡ new best
          </span>
        )}
        <span
          className={`font-display text-7xl font-extrabold lg:text-8xl ${
            isBest ? "animate-glow text-accent" : ""
          }`}
        >
          {hits}
        </span>
        <span className="mt-1 text-sm text-text-muted lg:text-base">
          hits in 20s
        </span>
      </section>

      {submitted && (
        <p className="text-sm text-text">
          #{submitted.rank} of {submitted.total}
          {submitted.total >= 10 && (
            <span className="text-accent"> · top {submitted.percentile}%</span>
          )}
        </p>
      )}
      {submitError && <p className="text-sm text-accent">{submitError}</p>}

      {card ? (
        <ShareSheet
          card={card}
          replyLink="aim.karrtopelka.com/r"
          caption={`${hits} hits in 20s. ${card.provocation}`}
          loadAssets={loadCardAssets}
        />
      ) : (
        <button
          type="button"
          onClick={onPost}
          disabled={posting}
          className="w-full max-w-md animate-neon-pulse rounded-md bg-accent px-4 py-3 font-semibold text-bg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow disabled:opacity-40"
        >
          {posting ? "posting…" : "post your score"}
        </button>
      )}

      <button
        type="button"
        onClick={() => {
          setSubmitted(null);
          setCard(null);
          setSubmitError(null);
          reset();
        }}
        className="text-sm text-text-muted transition-colors duration-200 hover:text-accent"
      >
        play again
      </button>

      {/* on lg the persistent right rail owns the board — no double render */}
      <section className="flex w-full flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          best aim
        </h2>
        <Leaderboard
          entries={entries}
          loading={loading}
          myHandle={handle}
          formatValue={(v) => String(v)}
          emptyLabel="no scores yet — set the bar."
          range={range}
          onRange={setRange}
        />
      </section>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={(h) => {
            setShowHandle(false);
            onHandle(h);
            void doSubmit();
          }}
        />
      )}
    </div>
  );
}
